// Rebuilt in-campaign shell (style C), mounted at the App level OUTSIDE the
// legacy .cc-* hierarchy so legacy CSS cannot reach it. Loads campaign state
// itself and routes the four screens. Only "Мир" is rebuilt so far; the others
// show a placeholder until they are rebuilt one by one. Behind the ?gnui flag.

import { useCallback, useEffect, useState } from "react";
import { api, type CampaignSetup } from "../api";
import { dayProgress, phaseInfo } from "../cycle/shared";
import { WorldScreen, type GnTab } from "./WorldScreen";
import { ActionsScreen, type Workspace } from "./ActionsScreen";
import { ChronicleScreen } from "./ChronicleScreen";
import { HqScreen } from "./HqScreen";
import { OwnerPanel } from "./OwnerPanel";
import { OnboardingScreen, RoleRevealScreen } from "./OnboardingScreen";

const GEN = "/assets/game-ui/gen";
const NAV: { id: GnTab; label: string; icon: string }[] = [
  { id: "world", label: "Мир", icon: "nav_world" },
  { id: "actions", label: "Действия", icon: "nav_actions" },
  { id: "chronicle", label: "Хроника", icon: "nav_chronicle" },
  { id: "hq", label: "Штаб", icon: "nav_council" },
];

export function GnCampaignShell({
  campaignId,
  onBack,
}: {
  campaignId: string;
  onBack: () => void;
}) {
  const [setup, setSetup] = useState<CampaignSetup | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [tab, setTab] = useState<GnTab>("world");
  const [actionsWs, setActionsWs] = useState<Workspace>("actions");
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [roleSeen, setRoleSeen] = useState(() => {
    try {
      return localStorage.getItem(`citychronicle.roleseen.${campaignId}`) === "1";
    } catch {
      return true;
    }
  });

  const load = useCallback(async () => {
    try {
      setSetup(await api.campaignSetup(campaignId));
    } catch {
      /* keep last good state; polling will retry */
    }
  }, [campaignId]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [load]);

  const action = useCallback(
    async (label: string, run: () => Promise<unknown>): Promise<boolean> => {
      setBusy(label);
      try {
        await run();
        await load();
        return true;
      } catch {
        return false;
      } finally {
        setBusy(null);
      }
    },
    [load],
  );

  if (!setup) {
    return (
      <div id="gn-root" className="gn-root">
        <div className="gn-shell">
          <div className="gn-loading">Город просыпается…</div>
        </div>
      </div>
    );
  }

  // Онбординг: до READY показываем gn-подготовку (ожидание игроков → предложение
  // мира → голосование → генерация). После генерации — один раз реал роли, затем игра.
  if (setup.setupStage !== "READY") {
    return (
      <div id="gn-root" className="gn-root">
        <div className="gn-shell">
          <OnboardingScreen campaignId={campaignId} setup={setup} action={action} onBack={onBack} />
          {busy && <div className="gn-busy">{busy}</div>}
        </div>
      </div>
    );
  }
  if (!roleSeen && setup.myPrivateProfile) {
    const markSeen = () => {
      try {
        localStorage.setItem(`citychronicle.roleseen.${campaignId}`, "1");
      } catch {
        /* ignore */
      }
      setRoleSeen(true);
    };
    return (
      <div id="gn-root" className="gn-root">
        <div className="gn-shell">
          <RoleRevealScreen campaignId={campaignId} setup={setup} onEnter={markSeen} />
        </div>
      </div>
    );
  }

  // Экран перехода (не пускаем в неинтерактивную игру, держим ожидание). Показываем,
  // когда: цикла нет (первый авто-стартует после генерации); новый цикл в PREPARING
  // (генерируется вводная); прошлый цикл PUBLISHED, а кампания жива — значит идёт
  // фоновый резолв дня и подготовка следующего цикла. Для первого цикла — ручной фолбэк.
  const currentCycle = setup.cycle.current;
  const cycleConcluding =
    !currentCycle ||
    currentCycle.phase === "PREPARING" ||
    (currentCycle.phase === "PUBLISHED" && setup.campaign.status !== "FINISHED");
  if (cycleConcluding) {
    const firstCycle = !currentCycle || (currentCycle.phase === "PREPARING" && currentCycle.cycleNumber <= 1);
    return (
      <div id="gn-root" className="gn-root">
        <div className="gn-shell">
          <div className="gn-top">
            <button type="button" className="gn-back" onClick={onBack} aria-label="К кампаниям">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
            </button>
            <div className="gn-tt">
              <b>{setup.commandHub?.header.campaignTitle ?? setup.campaign.title}</b>
              <span>{setup.commandHub?.header.worldName ?? setup.world?.worldName ?? ""}</span>
            </div>
            <span className="gn-cog-spacer" aria-hidden="true" />
          </div>
          <div className="gn-screen">
            <div className="gn-body">
              <div className="gn-panel">
                <span className="gn-eyebrow">{firstCycle ? "Мир готов" : "День подведён"}</span>
                <div className="gn-loading">{firstCycle ? "Первый цикл готовится…" : "Следующий цикл готовится…"}</div>
                <p className="gn-omuted">
                  {firstCycle
                    ? "Совет вот-вот соберётся: генерируется вводная первого дня. Это займёт до минуты."
                    : "Итоги дня записаны в хронику. Готовится вводная нового цикла — до минуты."}
                </p>
                {setup.cycle.canStartCycle && (
                  <button type="button" className="gn-cta" onClick={() => void action("Запускаем цикл…", () => api.startCycle(campaignId))}>
                    Запустить первый цикл сейчас
                  </button>
                )}
              </div>
            </div>
          </div>
          {busy && <div className="gn-busy">{busy}</div>}
        </div>
      </div>
    );
  }

  const hub = setup.commandHub;
  const cycle = setup.cycle.current;
  const title = hub?.header.campaignTitle ?? "Кампания";
  const worldName = hub?.header.worldName ?? "";
  const pct = cycle ? dayProgress(cycle).percent : 0;
  const phaseLabel = cycle ? phaseInfo(cycle.phase).label : "";

  return (
    <div id="gn-root" className="gn-root">
      <div className="gn-shell">
      <div className="gn-top">
        <button type="button" className="gn-back" onClick={onBack} aria-label="К кампаниям">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
        </button>
        <div className="gn-tt">
          <b>{title}</b>
          <span>{worldName}</span>
        </div>
        {setup.canManage ? (
          <button type="button" className="gn-cog" aria-label="Настройки владельца" onClick={() => setOwnerOpen(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 13a7.9 7.9 0 0 0 0-2l2-1.6-2-3.4-2.4 1a7.9 7.9 0 0 0-1.7-1l-.4-2.5h-4l-.4 2.5a7.9 7.9 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.6a7.9 7.9 0 0 0 0 2l-2 1.6 2 3.4 2.4-1a7.9 7.9 0 0 0 1.7 1l.4 2.5h4l.4-2.5a7.9 7.9 0 0 0 1.7-1l2.4 1 2-3.4z" /></svg>
          </button>
        ) : (
          <span className="gn-cog-spacer" aria-hidden="true" />
        )}
      </div>

      {cycle && tab !== "actions" && (
        <div className="gn-daystrip">
          <span className="gn-dl">Цикл {cycle.cycleNumber} · {phaseLabel}</span>
          <span className="gn-bar"><i style={{ width: `${pct}%` }} /></span>
          <span className="gn-dl"><b>{pct}%</b></span>
        </div>
      )}

      {tab === "world" ? (
        <div className="gn-screen">
          <WorldScreen
            setup={setup}
            onNavigate={(t, w) => {
              setTab(t);
              if (w) setActionsWs(w);
            }}
          />
        </div>
      ) : tab === "actions" ? (
        <ActionsScreen campaignId={campaignId} setup={setup} action={action} ws={actionsWs} setWs={setActionsWs} />
      ) : tab === "chronicle" ? (
        <div className="gn-screen">
          <ChronicleScreen campaignId={campaignId} setup={setup} />
        </div>
      ) : (
        <div className="gn-screen">
          <HqScreen campaignId={campaignId} setup={setup} action={action} />
        </div>
      )}

      <div className="gn-nav">
        {NAV.map((n) => (
          <button key={n.id} type="button" className={tab === n.id ? "on" : ""} onClick={() => setTab(n.id)}>
            <img src={`${GEN}/${n.icon}.png`} alt="" />
            {n.label}
          </button>
        ))}
      </div>

      {ownerOpen && setup.canManage && (
        <OwnerPanel campaignId={campaignId} setup={setup} action={action} onClose={() => setOwnerOpen(false)} />
      )}

      {busy && <div className="gn-busy">{busy}</div>}
      </div>
    </div>
  );
}
